import React, { useEffect, useState, createElement, Component } from 'react';
import { ExternalLinkIcon } from 'lucide-react';
import { Button } from '../components/Button';
interface FeedItem {
  type: 'NEWS' | 'SOCIAL';
  source: string;
  title: string;
  description: string;
  link: string;
  pubDate: Date;
  domain: string;
}
const NEWS_FEEDS = [{
  name: 'CoinDesk',
  url: 'https://www.coindesk.com/arc/outboundfeeds/rss/?outputType=xml'
}, {
  name: 'CoinTelegraph',
  url: 'https://cointelegraph.com/rss'
}, {
  name: 'CryptoSlate',
  url: 'https://cryptoslate.com/feed/'
}];
const SOCIAL_FEEDS = [{
  name: 'r/CryptoCurrency',
  url: 'https://www.reddit.com/r/CryptoCurrency/.rss'
}, {
  name: 'r/Bitcoin',
  url: 'https://www.reddit.com/r/Bitcoin/.rss'
}, {
  name: 'r/CryptoMarkets',
  url: 'https://www.reddit.com/r/CryptoMarkets/.rss'
}];
const RSS2JSON_BASE = 'https://api.rss2json.com/v1/api.json?rss_url=';
function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}
function getDomain(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace('www.', '');
  } catch {
    return '';
  }
}
function getRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString();
}
export function SocialMediaPage() {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [displayCount, setDisplayCount] = useState(12);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  useEffect(() => {
    loadFeeds();
  }, []);
  async function fetchFeed(feed: (typeof NEWS_FEEDS)[0], type: 'NEWS' | 'SOCIAL'): Promise<FeedItem[]> {
    const url = RSS2JSON_BASE + encodeURIComponent(feed.url);
    const res = await fetch(url);
    if (!res.ok) throw new Error('Failed to fetch ' + feed.name);
    const data = await res.json();
    if (data.status !== 'ok') throw new Error('Bad status for ' + feed.name);
    return data.items.map((item: any) => ({
      type,
      source: feed.name,
      title: item.title,
      description: stripHtml(item.description || item.content || ''),
      link: item.link,
      pubDate: new Date(item.pubDate || item.pubdate || item.date || Date.now()),
      domain: getDomain(item.link)
    }));
  }
  async function loadFeeds() {
    try {
      const newsPromises = NEWS_FEEDS.map(feed => fetchFeed(feed, 'NEWS'));
      const socialPromises = SOCIAL_FEEDS.map(feed => fetchFeed(feed, 'SOCIAL'));
      const results = await Promise.allSettled([...newsPromises, ...socialPromises]);
      let allItems: FeedItem[] = [];
      results.forEach(result => {
        if (result.status === 'fulfilled') {
          allItems = allItems.concat(result.value);
        }
      });
      allItems.sort((a, b) => b.pubDate.getTime() - a.pubDate.getTime());
      if (allItems.length > 0) {
        setItems(allItems);
        setError(false);
      } else {
        setError(true);
      }
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }
  function loadMore() {
    setDisplayCount(prev => prev + 8);
  }
  if (loading) {
    return <div className="min-h-screen bg-slate-50" data-id="element-546">
        <div className="bg-gradient-to-br from-slate-900 to-blue-900 text-white py-12" data-id="element-547">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-548">
            <h1 className="text-4xl font-bold mb-3" data-id="element-549">Crypto Social Feed</h1>
            <p className="text-slate-300 text-lg" data-id="element-550">
              Latest news and discussions from the crypto community
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-551">
          <div className="flex flex-col items-center justify-center gap-4 py-20" data-id="element-552">
            <div className="flex gap-2" data-id="element-553">
              <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{
              animationDelay: '0ms'
            }} data-id="element-554"></div>
              <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{
              animationDelay: '150ms'
            }} data-id="element-555"></div>
              <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{
              animationDelay: '300ms'
            }} data-id="element-556"></div>
            </div>
            <p className="text-slate-600 text-lg animate-pulse" data-id="element-557">
              Loading crypto social feed...
            </p>
          </div>
        </div>
      </div>;
  }
  if (error) {
    return <div className="min-h-screen bg-slate-50" data-id="element-558">
        <div className="bg-gradient-to-br from-slate-900 to-blue-900 text-white py-12" data-id="element-559">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-560">
            <h1 className="text-4xl font-bold mb-3" data-id="element-561">Crypto Social Feed</h1>
            <p className="text-slate-300 text-lg" data-id="element-562">
              Latest news and discussions from the crypto community
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-563">
          <div className="text-center py-20" data-id="element-564">
            <p className="text-slate-600 text-lg" data-id="element-565">
              Unable to load social feed right now. Please try again later.
            </p>
          </div>
        </div>
      </div>;
  }
  const displayedItems = items.slice(0, displayCount);
  const hasMore = displayCount < items.length;
  return <div className="min-h-screen bg-slate-50" data-id="element-566">
      <div className="bg-gradient-to-br from-slate-900 to-blue-900 text-white py-12" data-id="element-567">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-568">
          <h1 className="text-4xl font-bold mb-3" data-id="element-569">Crypto Social Feed</h1>
          <p className="text-slate-300 text-lg" data-id="element-570">
            Latest news and discussions from the crypto community
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-571">
        <div className="space-y-4" data-id="element-572">
          {displayedItems.map((item, index) => <div key={index} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow" data-id="element-573">
              {/* Header */}
              <div className="flex items-start gap-4 mb-4" data-id="element-574">
                {/* Avatar */}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0 ${item.type === 'NEWS' ? 'bg-blue-600' : 'bg-orange-600'}`} data-id="element-575">
                  {item.source.charAt(0).toUpperCase()}
                </div>

                {/* Source Info */}
                <div className="flex-1 min-w-0" data-id="element-576">
                  <div className="flex items-center gap-2 mb-1" data-id="element-577">
                    <span className="font-semibold text-slate-900" data-id="element-578">
                      {item.source}
                    </span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${item.type === 'NEWS' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-579">
                      {item.type}
                    </span>
                  </div>
                  <div className="text-sm text-slate-500" data-id="element-580">
                    {getRelativeTime(item.pubDate)}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="mb-4" data-id="element-581">
                <h3 className="font-bold text-lg text-slate-900 mb-2 line-clamp-2" data-id="element-582">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm line-clamp-3" data-id="element-583">
                  {item.description.slice(0, 250)}
                  {item.description.length > 250 ? '...' : ''}
                </p>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-584">
                <span className="text-xs text-slate-500" data-id="element-585">{item.domain}</span>
                <a href={item.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors" data-id="element-586">
                  <span data-id="element-587">Open Post</span>
                  <ExternalLinkIcon size={14} data-id="element-588" />
                </a>
              </div>
            </div>)}
        </div>

        {/* Load More Button */}
        {hasMore && <div className="text-center mt-8" data-id="element-589">
            <Button onClick={loadMore} size="lg" data-id="element-590">
              Load More Posts
            </Button>
          </div>}

        {/* End Message */}
        {!hasMore && items.length > 0 && <div className="text-center mt-8 text-slate-500" data-id="element-591">
            You've reached the end of the feed
          </div>}
      </div>
    </div>;
}